// Transporte Web Bluetooth (BLE) para impresión térmica ESC/POS.
//
// Servicio SIN React, portado de la lógica YA PROBADA contra hardware real del spike
// (plick-print-web-spike: flow.js / spike.js). Reúne: descubrimiento de característica
// escribible, escritura en chunks, reconexión silenciosa (getDevices) y reconexión
// asegurada antes de cada impresión (ensureReady) porque las térmicas BLE se duermen.
//
// REGLA CRÍTICA: nunca se accede a `navigator.bluetooth` en el top-level de este módulo
// (rompería los tests en jsdom, que no lo define). Todo acceso vive dentro de funciones,
// detrás de la guarda `isWebBleSupported()`.

import type {
  Bluetooth,
  BluetoothDevice,
  BluetoothRemoteGATTCharacteristic,
  BluetoothRemoteGATTServer,
} from '../../../types/web-bluetooth';

// UUIDs de servicios BLE de las térmicas conocidas (spike.js / flow.js). Se usan como
// `optionalServices` en requestDevice y para descubrir servicios con getPrimaryServices.
export const KNOWN_SERVICES: string[] = [
  '000018f0-0000-1000-8000-00805f9b34fb', // Goojprt / Zjiang / Munbyn / POS-58 (más común)
  '49535343-fe7d-4ae5-8fa9-9fafd205e455', // MTP-2/3 (ISSC / Microchip)
  '0000ae30-0000-1000-8000-00805f9b34fb', // CAT / mini
  '0000ff00-0000-1000-8000-00805f9b34fb', // PeriPage / FoMemo
  '0000ffe0-0000-1000-8000-00805f9b34fb', // BLE genéricos (HM-10 / JDY)
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Nordic-UART-like
  '6e400001-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART Service
];

// Tamaño de chunk de escritura (bytes) y respiro entre chunks (ms). El spike usaba 512/20, pero
// en hardware (PT-220) un payload grande se TRUNCABA tras el primer chunk: escribir 512 B por
// `writeValueWithoutResponse` (sin ACK) más rápido de lo que drena el enlace → se pierden los
// chunks siguientes. Bajamos el default a un tamaño que cabe en el MTU típico BLE (~185) y damos
// más respiro. Configurables sin redeploy vía localStorage (panel debug) para tunear por modelo.
const DEFAULT_CHUNK_SIZE = 180;
const DEFAULT_CHUNK_DELAY_MS = 30;

const getChunkSize = (): number => {
  try {
    const v = parseInt(typeof window !== 'undefined' ? window.localStorage?.getItem('webBleChunkSize') || '' : '', 10);
    return Number.isFinite(v) && v >= 20 && v <= 2048 ? v : DEFAULT_CHUNK_SIZE;
  } catch {
    return DEFAULT_CHUNK_SIZE;
  }
};

const getChunkDelay = (): number => {
  try {
    const v = parseInt(typeof window !== 'undefined' ? window.localStorage?.getItem('webBleChunkDelay') || '' : '', 10);
    return Number.isFinite(v) && v >= 0 && v <= 500 ? v : DEFAULT_CHUNK_DELAY_MS;
  } catch {
    return DEFAULT_CHUNK_DELAY_MS;
  }
};

// Conexión activa de un dispositivo físico: el device + su característica escribible.
export type BleConnection = {
  device: BluetoothDevice;
  writeChar: BluetoothRemoteGATTCharacteristic;
};

// Mapa de runtime deviceId -> conexión activa. Permite que varios perfiles lógicos
// (Caja, Cocina…) que apuntan al MISMO dispositivo físico compartan una sola conexión,
// y que ensureReady reutilice/reconecte sin re-emparejar. Vive en memoria del módulo;
// se reconstruye al recargar la página (los deviceId son volátiles entre sesiones).
const runtime = new Map<string, BleConnection>();

// Handles de dispositivos YA emparejados en esta sesión (deviceId -> BluetoothDevice). A
// diferencia de `runtime`, NO se borra al desconectarse: conservamos el handle para poder
// reconectar DIRECTO con `gatt.connect()` (sin `getDevices()` ni gesto del usuario) cuando
// la térmica se duerme entre impresiones. Solo se pierde al recargar la página.
const pairedDevices = new Map<string, BluetoothDevice>();

// Dispositivos a los que ya se les enganchó el listener `gattserverdisconnected`.
// Web Bluetooth reutiliza la MISMA instancia de BluetoothDevice entre reconexiones
// (getDevices / ensureReady), así que sin esta guarda los listeners se acumularían en
// cada attachDevice → fuga de memoria y N invocaciones por desconexión. WeakSet para no
// retener el device si el navegador lo descarta.
const disconnectListenerAttached = new WeakSet<BluetoothDevice>();

// Dispositivos a los que ya se les enganchó el listener `advertisementreceived` vía
// watchAndReconnect. El keep-alive (useWebBleKeepAlive) lo invoca en CADA reconexión, y
// como las térmicas BLE se duermen entre pedidos el ciclo despierta→reconecta→duerme es
// NORMAL: sin esta guarda se acumularían N listeners por device (cada anuncio dispararía N
// attachDevice en paralelo) → fuga. WeakSet para no retener el device si el navegador lo
// descarta. El unsubscriber retornado quita el device del set (permite re-observar tras un
// teardown explícito). `let` porque el WeakSet no tiene .clear(): se reasigna en el reset.
let advertisementWatchAttached = new WeakSet<BluetoothDevice>();

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// Guarda de soporte. NO accede en top-level: solo dentro de funciones. jsdom no define
// navigator.bluetooth, por lo que en tests devuelve false (camino seguro).
export const isWebBleSupported = (): boolean => typeof navigator !== 'undefined' && Boolean(navigator.bluetooth);

const getBluetooth = (): Bluetooth => {
  if (typeof navigator === 'undefined' || !navigator.bluetooth) {
    throw new Error('Web Bluetooth no disponible en este entorno.');
  }
  return navigator.bluetooth;
};

// Abre el chooser del navegador (requiere gesto de usuario). Si se pasa namePrefix,
// filtra para que solo aparezca la impresora del usuario; si no, acepta todos los
// dispositivos (flujo de primer emparejamiento del spike: addPrinter).
export const requestPrinter = async ({ namePrefix }: { namePrefix?: string } = {}): Promise<BluetoothDevice> => {
  const bluetooth = getBluetooth();
  if (namePrefix && namePrefix.trim()) {
    return bluetooth.requestDevice({
      filters: [{ namePrefix: namePrefix.trim() }],
      optionalServices: KNOWN_SERVICES,
    });
  }
  return bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: KNOWN_SERVICES,
  });
};

// Descubre la primera característica escribible (write o writeWithoutResponse) recorriendo
// los servicios primarios del servidor GATT (spike flow.js: findWritableChar).
const findWritableChar = async (
  server: BluetoothRemoteGATTServer,
): Promise<BluetoothRemoteGATTCharacteristic | null> => {
  for (const service of await server.getPrimaryServices()) {
    for (const characteristic of await service.getCharacteristics()) {
      if (characteristic.properties.write || characteristic.properties.writeWithoutResponse) {
        return characteristic;
      }
    }
  }
  return null;
};

// Conecta el GATT con reintentos (3 intentos, backoff 0/250/500ms). Las térmicas BLE que
// acaban de despertar rechazan el primer connect; reintentar sube mucho la tasa de éxito.
const connectWithRetry = async (gatt: BluetoothRemoteGATTServer): Promise<BluetoothRemoteGATTServer> => {
  let lastError: unknown;
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt > 0) await sleep(attempt * 250);
    try {
      return await gatt.connect();
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError instanceof Error ? lastError : new Error('No se pudo conectar el GATT.');
};

// Conecta un dispositivo físico, descubre su característica escribible y lo registra en
// el runtime por su deviceId (spike flow.js: attachDevice). Idempotente respecto al GATT:
// si ya está conectado, reutiliza el servidor.
export const attachDevice = async (device: BluetoothDevice): Promise<BleConnection> => {
  if (!device.gatt) {
    throw new Error('El dispositivo no expone GATT.');
  }
  // Conserva el handle para reconexiones directas (sobrevive a las desconexiones).
  pairedDevices.set(device.id, device);
  // Limpia la conexión del runtime cuando el dispositivo se desconecta (se dormirá entre
  // pedidos). ensureReady la reconstruirá en la próxima impresión. Se engancha UNA sola
  // vez por instancia de device (attachDevice se llama repetido entre reconexiones).
  if (!disconnectListenerAttached.has(device)) {
    device.addEventListener('gattserverdisconnected', () => {
      runtime.delete(device.id);
    });
    disconnectListenerAttached.add(device);
  }
  // Conexión con reintentos: las térmicas BLE recién despiertas suelen RECHAZAR el primer
  // gatt.connect() (NetworkError) y aceptar al 2º/3º intento. Sin reintento, "Confirmar"
  // fallaba con la impresora justo despertando. Backoff corto entre intentos.
  const server = device.gatt.connected ? device.gatt : await connectWithRetry(device.gatt);
  const writeChar = await findWritableChar(server);
  if (!writeChar) {
    throw new Error('Impresora conectada pero sin característica escribible.');
  }
  const connection: BleConnection = { device, writeChar };
  runtime.set(device.id, connection);
  return connection;
};

// Reconexión SIN diálogo usando navigator.bluetooth.getDevices() (dispositivos ya
// autorizados por el usuario). Devuelve null si getDevices no está disponible o si la
// impresora guardada no se encuentra (spike flow.js: rama getDevices de confirm).
export const reconnectKnown = async ({
  deviceId,
  deviceName,
}: {
  deviceId?: string;
  deviceName?: string;
}): Promise<BleConnection | null> => {
  if (!isWebBleSupported()) return null;
  // Reuso directo: el dispositivo ya está en runtime y conectado (otro perfil lo abrió).
  if (deviceId) {
    const existing = runtime.get(deviceId);
    if (existing && existing.device.gatt?.connected) return existing;
    // Tenemos el handle de esta sesión aunque esté desconectado → reconectar DIRECTO con
    // gatt.connect() (no requiere getDevices ni gesto). Cubre "se durmió tras emparejar".
    const held = pairedDevices.get(deviceId);
    if (held) {
      try {
        return await attachDevice(held);
      } catch {
        /* sigue al fallback de getDevices */
      }
    }
  }
  const bluetooth = getBluetooth();
  if (!bluetooth.getDevices) return null;
  let known: BluetoothDevice[];
  try {
    known = await bluetooth.getDevices();
  } catch {
    // getDevices puede fallar (flag desactivado / permisos). Degradar a null: el caller
    // hará fallback a requestPrinter (chooser filtrado) dentro de un gesto de usuario.
    return null;
  }
  const match =
    (deviceId ? known.find((d) => d.id === deviceId) : undefined) ??
    (deviceName ? known.find((d) => d.name === deviceName) : undefined);
  if (!match) return null;
  return attachDevice(match);
};

// Garantiza una característica escribible lista ANTES de cada impresión. Las térmicas BLE
// se duermen entre pedidos: si el GATT no está conectado, reconecta en silencio
// (spike flow.js: ensureReady). Lanza si el device no tiene GATT.
export const ensureReady = async (device: BluetoothDevice): Promise<BluetoothRemoteGATTCharacteristic> => {
  const existing = runtime.get(device.id);
  if (existing && existing.device.gatt?.connected && existing.writeChar) {
    return existing.writeChar;
  }
  const connection = await attachDevice(device);
  return connection.writeChar;
};

// Lock de serialización a NIVEL DE MÓDULO. Cada componente/card monta su propia instancia
// de useWebBlePrint (con su isPrinting local), así que el guard de UI no coordina entre
// ellos: dos impresiones a la MISMA característica intercalarían sus chunks → ticket
// corrupto. Encadenar aquí garantiza orden estricto sin descartar ninguna impresión.
let writeLock: Promise<void> = Promise.resolve();

// Escribe los bytes en chunks de CHUNK_SIZE. Prefiere writeValueWithoutResponse cuando la
// característica solo soporta sin-respuesta (más rápido y único modo en muchas térmicas);
// si no, writeValue. Respira CHUNK_DELAY_MS entre chunks (spike flow.js: writeChunked).
// Serializada: una escritura espera a que termine la anterior (ver writeLock).
export const writeChunked = async (writeChar: BluetoothRemoteGATTCharacteristic, bytes: Uint8Array): Promise<void> => {
  const chunkSize = getChunkSize();
  const chunkDelay = getChunkDelay();
  const run = async (): Promise<void> => {
    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
      const part = bytes.slice(offset, offset + chunkSize);
      if (
        writeChar.properties.writeWithoutResponse &&
        !writeChar.properties.write &&
        typeof writeChar.writeValueWithoutResponse === 'function'
      ) {
        await writeChar.writeValueWithoutResponse(part);
      } else {
        await writeChar.writeValue(part);
      }
      await sleep(chunkDelay);
    }
  };
  // Encadena tras la escritura previa (haya resuelto o fallado) y avanza el lock.
  const result = writeLock.then(run, run);
  writeLock = result.catch(() => undefined);
  return result;
};

// Reconexión proactiva sin gesto vía watchAdvertisements (cuando el navegador lo soporta).
// Degrada en silencio si no está disponible. Devuelve una función para dejar de escuchar.
// Útil para auto-print por socket: la impresora vuelve a anunciarse al despertar.
export const watchAndReconnect = (
  device: BluetoothDevice,
  onReconnected: (connection: BleConnection) => void,
): (() => void) => {
  if (typeof device.watchAdvertisements !== 'function') {
    return () => undefined;
  }
  // Idempotencia: si ya estamos observando este device, no re-registramos listener ni
  // re-llamamos watchAdvertisements (el keep-alive invoca esto en cada reconexión y el
  // device se reutiliza entre ellas → si no, se acumularían listeners). No-op silencioso.
  if (advertisementWatchAttached.has(device)) {
    return () => undefined;
  }
  const handler = (): void => {
    if (device.gatt?.connected) return;
    attachDevice(device)
      .then(onReconnected)
      .catch(() => undefined);
  };
  device.addEventListener('advertisementreceived', handler);
  advertisementWatchAttached.add(device);
  device.watchAdvertisements().catch(() => undefined);
  // El unsubscriber quita el device del set: tras un teardown explícito se puede volver a
  // observar. NO lo quitamos dentro de `handler` (las reconexiones del keep-alive no son
  // teardowns y deben seguir cubiertas por la misma observación).
  return () => {
    device.removeEventListener('advertisementreceived', handler);
    advertisementWatchAttached.delete(device);
  };
};

// Helpers de runtime para que el hook React maneje el ciclo de vida sin reimplementar el Map.
export const getConnection = (deviceId: string): BleConnection | undefined => runtime.get(deviceId);

// DEBUG: describe los modos de escritura de la característica del dispositivo. 'write(ACK)' =
// con respuesta (fiable, con control de flujo); 'writeNoResp' = sin respuesta (rápido pero puede
// truncar payloads grandes sin pacing). '—' si no hay conexión viva.
export const describeWriteChar = (deviceId: string): string => {
  const props = runtime.get(deviceId)?.writeChar?.properties;
  if (!props) return '—';
  const modes: string[] = [];
  if (props.write) modes.push('write(ACK)');
  if (props.writeWithoutResponse) modes.push('writeNoResp');
  return modes.length ? modes.join('+') : 'otro';
};

// DEBUG: config actual de troceo del transporte (chunk + delay), incluyendo overrides de debug.
export const currentChunkConfig = (): { size: number; delay: number } => ({
  size: getChunkSize(),
  delay: getChunkDelay(),
});

export const isConnected = (deviceId: string): boolean => {
  const connection = runtime.get(deviceId);
  return Boolean(connection && connection.device.gatt?.connected && connection.writeChar);
};

export const disconnect = (deviceId: string): void => {
  const connection = runtime.get(deviceId);
  if (connection && connection.device.gatt?.connected) {
    connection.device.gatt.disconnect();
  }
  runtime.delete(deviceId);
};

// Solo para tests: limpia el Map de runtime (no toca hardware ni localStorage).
export const __resetRuntimeForTests = (): void => {
  runtime.clear();
  pairedDevices.clear();
  // WeakSet no expone .clear(): reasignamos uno nuevo para soltar las guardas de observación.
  advertisementWatchAttached = new WeakSet<BluetoothDevice>();
  writeLock = Promise.resolve();
};
